import { useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { startOfPreviousMonth } from "../../../../../utils/typeUtils"
import { Action, Async, Badge, Column, FieldType, FieldValue, FilterField2, FiltersOptions, ModalId, O, option, PanelWrapper, UIPermission, useUIActions } from "../../../../../features/ui"
import { RoutePath } from "../../../../../features/routes"
import { Data2, DataTypeList3, Labels, useDescribeHooks } from "../../../../../features/data-types"
import { DataType, TableName } from "../../../../../tables"
import { Params } from "../../../../../state/reducers/types"
import { BranchBadge, useManagerHooks } from "../../../../manager"
import { useOperationLoaders } from "../../../../operations"
import { deliveryOrderHelpers, useDeliveryOrderHooks } from "../../../hooks/deliveryOrderHooks"
import { useDeliveryOrderLoaders } from "../../../loaders/deliveryOrderLoaders"
import { DeliveryMethod, DeliveryOrder, DeliveryStatus } from "../../../state/types"
import { useSystemStateHooks } from "../../../../system"
import { deliveryHelpers } from "../../../hooks/deliveryHooks"
import { useDeliveryLoaders } from "../../../loaders/deliveryLoaders"
import { DownloadDeliveryOrderNotesModal } from "../download-delivery-order-notes-modal/downloadDeliveryOrderNotesModal"
import { useSupplierLoaders } from "../../../../purchases"
import { DeliveryOrderStatusFilter } from "../../../hooks/types"
import { BookmarkButton } from "../../../../../features/bookmarks"

// TODO: antes de migrar dev, migrar datos de morris migrados a alguna company en local
// probar a fondo esta list para ver si andan bien filtros y logica de batch.

type Props = {
    method: DeliveryMethod
}

export const DeliveryOrderList = (props: Props) => {
    const { method } = props
    const isReception = useMemo(() => method === DeliveryMethod.RECEPTION, [method])
    const isShipment = useMemo(() => method === DeliveryMethod.SHIPMENT, [method])

    const [currentDeliveryOrder, setCurrentDeliveryOrder] = useState<O<DeliveryOrder>>()

    const { isDeliverable, isDeliveryCompletable, isAnnulable } = deliveryOrderHelpers
    const { getCreateDeliveryIcon, getCreateDeliveryLabel } = deliveryHelpers
    const { userHasPermission } = useSystemStateHooks()
    const readOnly = !userHasPermission(UIPermission.SAVE_SALE_DELIVERIES)
    const { currentBranchId } = useManagerHooks()
    const { loadDeliveryOrdersBatch, loadBatchData } = useDeliveryOrderHooks()
    const { describe } = useDescribeHooks()
    const navigate = useNavigate()
    const { pathname } = useLocation()

    const deliveryOrderLoaders = useDeliveryOrderLoaders()
    const deliveryLoaders = useDeliveryLoaders()
    const operationLoaders = useOperationLoaders()
    const supplierLoaders = useSupplierLoaders()

    const { openModal } = useUIActions()

    const title = useMemo(() => `Órdenes de ${Labels.deliveryMethodLabel(method)}`, [method])

    const allTables = 'ALL'
    const tableOptions = useMemo(() => [
        option(allTables, "Todas"),
        option(TableName.SALE_ORDERS, "Ventas"),
        option(TableName.HOARD_ORDERS, "Acopios")
    ], [])

    const filterFields: FilterField2[] = useMemo(() => {
        const filterFieldList: FilterField2[] = [
            {
                name: 'deliveryStatus',
                type: FieldType.SELECT,
                options: [
                    option(DeliveryOrderStatusFilter.NOT_DELIVERED, 'No Entregadas'),
                    ...Object.values(DeliveryStatus).map(status => option(status, Labels.deliveryStatusLabel(status))),
                    option(DeliveryOrderStatusFilter.CANCELLED, 'Canceladas')
                ],
                defaultValue: DeliveryOrderStatusFilter.NOT_DELIVERED,
                label: 'Estado de Entrega',
                space: isReception ? 4 : 3
            },
            {
                name: 'creationDateFrom',
                type: FieldType.DATE,
                label: 'Fecha de Creación: Desde',
                space: 2
            },
            {
                name: 'creationDateTo',
                type: FieldType.DATE,
                label: 'Fecha de Creación: Hasta',
                space: 2
            },
            {
                name: 'dateFrom',
                type: FieldType.DATE,
                label: 'Fecha de Entrega: Desde',
                space: 2
            },
            {
                name: 'dateTo',
                type: FieldType.DATE,
                label: 'Fecha de Entrega: Hasta',
                space: 2
            }
        ]
        if (!isReception) {
            const tableField = {
                name: 'table',
                type: FieldType.SELECT,
                label: 'Tipo de Operación',
                options: tableOptions,
                space: 2
            }
            const customerField = {
                name: 'customerId',
                type: FieldType.TABLE,
                table: TableName.CUSTOMERS,
                label: 'Cliente',
                space: 4
            }
            filterFieldList.splice(0, 0, tableField, customerField)
        }

        return filterFieldList
    }, [tableOptions, isReception])

    const filtersOptions: FiltersOptions = useMemo(() => ({
        filtersKey: 'delivery-orders',
        search: {
            placeholder: 'Buscar por código'
        },
        filterFields,
        // Por defecto fecha de entrega desde el mes anterior, sin fecha de fin para no ocultar órdenes programadas
        defaultFiltersData: { dateFrom: startOfPreviousMonth() },
        filtersTextAffix: currentBranchId,
        firstRowLength: 3
    }), [filterFields, currentBranchId])

    const columns: Column[] = [
        {
            name: 'code',
            type: FieldType.TEXT,
            label: '#',
            render: (value?: FieldValue) => `#${value}`
        },
        {
            name: 'operationId',
            type: FieldType.TEXT,
            label: 'Operación',
            // Se carga de forma asíncrona: ordenaría por el id y no por lo que se ve
            sortable: false,
            render: (value?: FieldValue) => <Async
                loadFn={async () => {
                    const deliveryOrder = await deliveryOrderLoaders().loadDeliveryOrder(value! as string)
                    const operation = await operationLoaders().loadOperation(deliveryOrder!.table, deliveryOrder!.operationId)
                    return <>{describe(deliveryOrder!.table, operation!)}</>
                }}
            />
        },
        {
            name: 'deliveryDate',
            type: FieldType.DATE,
            label: 'Fecha de Entrega'
        },
        {
            name: 'deliveryStatus',
            type: FieldType.TEXT,
            label: 'Estado de Entrega',
            sortValue: (value?: FieldValue) => Labels.deliveryStatusLabel(value as DeliveryStatus),
            render: (value?: FieldValue) => Labels.deliveryStatusLabel(value! as DeliveryStatus)
        },
        {
            name: 'cancelled',
            type: FieldType.TEXT,
            label: '',
            render: (value?: FieldValue) => value ? <Badge label="Cancelada" /> : <></>
        }
    ]
    if (isReception) {
        const branchColumn = {
            name: 'branchId',
            type: FieldType.TABLE,
            table: TableName.BRANCHES,
            label: 'Sucursal de Recepción',
            render: (value?: FieldValue) => <BranchBadge branchId={value! as string} />
        }
        columns.splice(2, 0, branchColumn)
        const supplierColumn = {
            name: 'supplierId',
            type: FieldType.TABLE,
            table: TableName.SUPPLIERS,
            label: 'Proveedor',
            render: (value?: FieldValue) => <Async
                loadFn={async () => {
                    const supplier = await supplierLoaders().loadSupplier(value as string)
                    return <Badge label={supplier!.name} />
                }}
            />
        }
        columns.splice(4, 0, supplierColumn)
    } else {
        const customerColumn = {
            name: 'customerId',
            type: FieldType.TABLE,
            table: TableName.CUSTOMERS,
            label: 'Cliente'
        }
        columns.splice(3, 0, customerColumn)
    }

    const toRow = (deliveryOrderDT: DataType): Data2 => {
        const { items, ...data } = deliveryOrderDT as DeliveryOrder
        return {
            ...data,
            operationId: deliveryOrderDT.id
        }
    }

    const onDownloadDeliveryNotes = (deliveryOrder: DeliveryOrder) => () => {
        setCurrentDeliveryOrder(deliveryOrder)
        openModal(ModalId.DOWNLOAD_FILES)
    }

    const onCreateDelivery = (deliveryOrderId: string) => () =>
        navigate(`${deliveryOrderId}/${isShipment ? RoutePath.GENERATE_DELIVERY_NOTE : RoutePath.COMPLETE_DELIVERY}`)

    const onCompleteDelivery = (deliveryOrderId: string) => () =>
        navigate(`${deliveryOrderId}/${RoutePath.COMPLETE_DELIVERY}`)

    const onAnnul = (deliveryOrderId: string) => () => {
        navigate(deliveryOrderId)
        setTimeout(() => {
            openModal(ModalId.ANNUL_DELIVERY_ORDER)
        }, 500)
    }

    const actions = async (rowIdValue: string) => {
        const deliveryOrder = (await deliveryOrderLoaders().loadDeliveryOrder(rowIdValue))!
        const deliveries = await deliveryLoaders().loadOrderDeliveries(deliveryOrder.id)
        const hasDeliveries = deliveries.length > 0
        const hasSignedDeliveryNotes = deliveries.some(delivery => delivery.deliveryNoteUrl)

        const actionList: Action[] = [
            { icon: 'eye-fill', kind: 'tertiary', tooltip: 'Ver Detalle', href: `${pathname}/${rowIdValue}` }
        ]
        if (isReception ? hasSignedDeliveryNotes : hasDeliveries) {
            actionList.push({ icon: 'download', tooltipLines: ['Descargar', 'Remitos'], kind: 'tertiary', onClick: onDownloadDeliveryNotes(deliveryOrder) })
        }
        if (!readOnly) {
            if (isDeliverable(deliveryOrder)) {
                actionList.push({
                    icon: getCreateDeliveryIcon(deliveryOrder.method),
                    tooltipLines: getCreateDeliveryLabel(deliveryOrder.method).split(' '),
                    onClick: onCreateDelivery(rowIdValue)
                })
            }
            if (isShipment && isDeliveryCompletable(deliveryOrder)) {
                actionList.push({ icon: 'bag-check-fill', tooltipLines: ['Completar', 'Envío'], onClick: onCompleteDelivery(rowIdValue) })
            }
            if (isAnnulable(deliveryOrder!)) {
                actionList.push({ icon: 'slash-circle', tooltipLines: ['Anular', 'Entrega'], kind: 'secondary', onClick: onAnnul(rowIdValue) })
            }
        }

        return actionList
    }

    const loadBatch = (
        filtersData?: Data2,
        batchSize?: number,
        startKey?: Params
    ) => {
        const updatedFiltersData = {
            ...filtersData,
            method,
            table: filtersData?.table === allTables ? undefined : filtersData?.table
        }
        return loadDeliveryOrdersBatch(updatedFiltersData, batchSize, startKey)
    }

    return (
        <PanelWrapper
            title={title}
            wide
            titleRight={<BookmarkButton routePath={
                method === DeliveryMethod.SHIPMENT ? RoutePath.SHIPMENT_ORDERS : (
                    method === DeliveryMethod.PICKUP ? RoutePath.PICKUP_ORDERS : RoutePath.RECEPTION_ORDERS
                )
            }/>}
        >
            <DataTypeList3
                columns={columns}
                filtersOptions={filtersOptions}
                canSort={filtersData => !!filtersData.dateFrom || !!filtersData.creationDateFrom || !!filtersData.customerId}
                sortDisabledHint={isReception ? 'Para ordenar, filtrá por fecha' : undefined}
                readOnly
                toRow={toRow}
                actions={actions}
                loadBatch={loadBatch}
                loadBatchData={loadBatchData}
            />
            <DownloadDeliveryOrderNotesModal deliveryOrder={currentDeliveryOrder} />
        </PanelWrapper>
    )
}
